import { useState, type ReactElement } from 'react';
import { track, trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { emberApi, type Knock, type RoomInviteRow, type RoomYes } from '../api';
import { hourLine } from '../letter-format';
import { navigate } from '../router';
import type { KnockCardFrame } from './KnockCard';
import { letterState } from './knock-letters';
import { PresenceAvatar } from './PresenceAvatar';
import { notifyKnocksChanged } from './use-knocks';
import './RoomInvitation.css';

/** "A", "A and B", "A, B and C". */
function withNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** What the invitation's body needs of the room and of this person's seat (RoomPageResponse and RoomInviteRow both carry it). */
export interface RoomInvitationProps {
  roomId: string;
  me: { name: string; avatar: string; via: 'intent' | 'availability' | 'standby' };
  /** How long a name here can be (server ROOM.NAME_MAX_CHARS): the name is theirs to change on the invitation (2026-10-20). */
  nameMaxChars?: number;
  /** When the room is already open (a join, 2026-09-26): how many are there and since when, so the page says so. */
  open?: { people: number; since: string | null } | null;
  /** The others who have already said yes and are waiting on this person (2026-09-28 拍板): named above the seat, so the yes reads as bringing the room closer, not as a maybe. */
  yeses?: RoomYes[];
  /** The room is scheduled at this many yeses (server ROOM.MIN_MEMBERS; three since 2026-09-29): with `yeses`, whether this yes is the one or more are still to come. */
  opensOnYeses?: number;
  /** The hour already set when there is one (2026-09-30 拍板; ISO, drawn on this browser's clock): a late invitation into a scheduled room says it. */
  opensAt?: string | null;
  /** After the server took the answer: yes with whether the room was scheduled or seated them, or no. */
  onAnswered: (answer: 'yes' | 'no', out: { opened?: boolean; joined?: boolean; waiting?: boolean; scheduled?: boolean; opens_at?: string }) => void;
  /** The name changed here: the page keeps it for what it draws after the answer. */
  onRenamed?: (name: string) => void;
}

/** How many yeses are still to come after this person's, given the others' already in. */
export function yesesToCome(opensOnYeses: number, yeses: number): number {
  return Math.max(0, opensOnYeses - 1 - yeses);
}

/** After a yes given while the room is still forming: how many more yeses it waits for, as a sentence (at least one, or the hour would be set). */
export function moreToOpen(opensOnYeses: number, yeses: number): string {
  const n = Math.max(1, yesesToCome(opensOnYeses, yeses));
  return n === 1 ? 'The room opens when one more person says yes.' : `The room opens when ${n} more people say yes.`;
}

/**
 * The invitation to a room (v4.0 §4.21; 2026-09-25 四次拍板 the seat card,
 * the same evening the paper; 2026-10-03 拍板 the seat alone): one seat card
 * — the picture and the name this person would go by in the room, theirs to
 * change right there (2026-10-20 founder: "Your seat" over a name read as a
 * puzzle, and a name you cannot change before you are in reads as assigned)
 * — one dark button, `Not this one` under it, and the small print. No hour is
 * asked (2026-10-20 founder): Ember sets it at the third yes and says it. The
 * body is the same on /rooms/:id and on the letter's paper; what surrounds it
 * (the flame and the room's name, or Ember's hand and the headline) belongs
 * to the page.
 */
export function RoomInvitation({ roomId, me, nameMaxChars = 24, open = null, yeses = [], opensOnYeses = 3, opensAt = null, onAnswered, onRenamed }: RoomInvitationProps): ReactElement {
  const [answering, setAnswering] = useState(false);
  const [refused, setRefused] = useState('');
  const [name, setName] = useState(me.name);
  const [naming, setNaming] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameRefused, setNameRefused] = useState('');

  // The same call, the same refusals as the door's (RoomThread saveName).
  const saveName = async (): Promise<void> => {
    const next = (naming || '').trim();
    if (!next || renaming) return;
    setRenaming(true); setNameRefused('');
    try {
      const out = await emberApi.renameInRoom(roomId, next);
      track('ui.clicked', { name: 'room_rename', state: 'invitation', match_id: roomId });
      setName(out.name);
      setNaming(null);
      onRenamed?.(out.name);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      trackFailed('room_rename', body?.error || 'error');
      setNameRefused(body?.error === 'name_taken' ? 'That one’s taken here. Pick another.' : body?.error === 'text_rejected' ? 'That one can’t be a name here. Pick another.' : body?.error === 'name_too_long' ? `Keep it under ${nameMaxChars} characters.` : body?.error === 'rename_limited' ? 'That’s ten name changes today. Try again tomorrow.' : 'Couldn’t change that. Try again.');
    } finally { setRenaming(false); }
  };

  const answer = async (accept: boolean): Promise<void> => {
    if (answering) return;
    setAnswering(true);
    try {
      const out = await emberApi.answerRoom(roomId, accept);
      track('ui.clicked', { name: accept ? 'room_invite_yes' : 'room_invite_no', match_id: roomId });
      notifyKnocksChanged();
      onAnswered(accept ? 'yes' : 'no', out);
    } catch {
      trackFailed('room_invite', 'error');
      setRefused('Couldn’t save that. Try again.');
    } finally { setAnswering(false); }
  };

  const waiting = open ? [] : yeses;
  // Whether this yes is the one that sets the hour, or more are still to come after it (2026-09-29 拍板: three; the line said "your yes opens the room" at any count).
  const toCome = yesesToCome(opensOnYeses, waiting.length);
  return (
    <div className="room-invitation">
      {waiting.length > 0 && (
        <p className="room-waiting-line" data-log-seen="room_invite_waiting" data-log-state={toCome === 0 ? 'opens' : 'more_to_come'} data-log-match-id={roomId}>
          <span className="room-waiting-faces" aria-hidden>
            {waiting.map((y) => <PresenceAvatar key={y.name} avatarKey={y.avatar} size={28} />)}
          </span>
          <span dir="auto">
            <b>{withNames(waiting.map((y) => y.name))} {waiting.length === 1 ? 'has' : 'have'} already said yes</b>{toCome === 0
              ? <> and {waiting.length === 1 ? 'is' : 'are'} waiting for you.</>
              : <>. {toCome === 1 ? 'One more yes after yours' : `${toCome} more yeses after yours`} and the room is on.</>}
          </span>
        </p>
      )}
      <div className="room-seat-card">
        <PresenceAvatar avatarKey={me.avatar} size={48} />
        <span className="room-seat-label">You’ll go by</span>
        {naming === null ? (
          <>
            <span className="room-seat-name" dir="auto">{name}</span>
            <button type="button" className="room-seat-change" onClick={() => { setNaming(name); setNameRefused(''); }}>Change name</button>
          </>
        ) : (
          <form className="room-seat-form" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
            <input className="room-seat-field" value={naming} maxLength={nameMaxChars} onChange={(e) => setNaming(e.target.value)} aria-label="Your name in rooms" autoFocus />
            <div className="room-seat-form-acts">
              <button type="submit" className="room-seat-save" disabled={renaming || !naming.trim()}>Save</button>
              <button type="button" className="room-link" onClick={() => { setNaming(null); setNameRefused(''); }}>Keep {name}</button>
            </div>
          </form>
        )}
        {nameRefused && <p className="room-refused room-seat-refused" role="alert">{nameRefused}</p>}
      </div>
      {opensAt && !open && (
        <p className="room-hours-set" dir="auto">The room opens <b>{hourLine(opensAt)}</b>. Come for the first few minutes if you can; after that, any hour is a good hour.</p>
      )}
      {refused && <p className="room-refused" role="alert">{refused}</p>}
      <div className="room-btns">
        <button type="button" className="room-btn primary" disabled={answering} onClick={() => { void answer(true); }}>I’ll sit with them</button>
        <button type="button" className="room-link" disabled={answering} onClick={() => { void answer(false); }}>Not this one</button>
      </div>
      <p className="room-foot">
        {open
          ? <><b>This room is already open.</b> {open.people === 1 ? 'One person is' : `${open.people} people are`} there with Ember{open.since ? ` since ${new Date(open.since).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : ''}; you can read what came before. </>
          : <><b>Three or four people and Ember, for a few days.</b> Others may join in the first two days and read what came before. </>}
        They’ll know you as {name} and nothing more. This invitation waits three days.{me.via === 'availability' && ' It came because you said in Settings you’d be open to this.'}{me.via === 'standby' && ' It came because you stand by for rooms like this one.'}
      </p>
    </div>
  );
}

/**
 * The invitation on the letter's paper (§4.21 「答法三处同」: the paper's
 * two buttons), in the knock card's clothes: the room's name and the reason,
 * then the same body as /rooms/:id. A yes that opens the room or seats the
 * person goes straight to the room in the talk page; a yes that waits for
 * more yeses or for the hour, or a no, settles the paper into its sentence.
 */
export function RoomInviteCard({ row, onChange, frame = 'card' }: {
  row: RoomInviteRow;
  onChange?: (next: Knock) => void;
  frame?: KnockCardFrame;
}): ReactElement {
  const { room } = row;
  const [outcome, setOutcome] = useState<{ opens_at?: string } | null>(null);
  return (
    <section
      className={`knock-card is-room${frame === 'letter' ? ' is-letter' : ''}`}
      aria-label="An invitation to a room"
      data-log-seen="knock_card"
      data-log-state={letterState(row)}
      data-log-match-id={row.match_id}
    >
      {room.name ? <p className="room-card-name" dir="auto">{room.name}</p> : null}
      <p className="room-card-reason" dir="auto">{room.fork}</p>
      {row.answer === null ? (
        <RoomInvitation
          roomId={room.id}
          me={{ name: room.my_name, avatar: room.avatar, via: room.via }}
          nameMaxChars={room.name_max_chars}
          yeses={room.yeses ?? []}
          opensOnYeses={room.opens_on_yeses}
          opensAt={room.opens_at}
          onAnswered={(answer, out) => {
            setOutcome(out);
            onChange?.({ ...row, letter: null, answer });
            if (answer === 'yes' && (out.opened || out.joined)) navigate(`/talk?mode=chat&room=${encodeURIComponent(room.id)}`);
          }}
        />
      ) : (
        <p className="room-card-settled">
          {row.answer === 'yes'
            ? `${(outcome?.opens_at || room.opens_at) ? `The room opens ${hourLine(outcome?.opens_at || room.opens_at)}; Ember will let you know when it opens.` : `${moreToOpen(room.opens_on_yeses, (room.yeses ?? []).length)} Ember will let you know.`} The door is in your chats.`

            : 'Ember will look again if you say the word.'}
        </p>
      )}
    </section>
  );
}
